<script setup lang="ts">
import type { PtwCommentDto, FileMetaInfoDto } from '@/api/generator';
import { EntityWithCommentType } from '@/api/generator';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import type { ContextMenuAction } from '@/components/ContextMenu/types';
import { getDefaultContextMenuActions } from '@/components/ContextMenu/utils';
import AvatarImage from '@/components/images/AvatarImage.vue';
import { useAccountStore } from '@/stores/accountStore';
import { useTranslation } from 'i18next-vue';
import { computed, ref } from 'vue';
import VeeAttachments from '../forms/Attachments/VeeAttachments.vue';
import { FormMode } from '../forms/types';
import { useVeeForm } from '../forms/useVeeForm';
import { useField } from 'vee-validate';
import Button from '@/components/buttons/Button.vue';
import CommentMetaInfo from './CommentMetaInfo.vue';
import Markdown from '@/components/Markdown/Markdown.vue';
import useCrudForComments from './useCrudForComments';
import { getDefaultDeleteConfirmationOptions } from '../confirmationModals/defaults';

// --- PROPS ---
const props = defineProps<{
  entityId: string | number;
  entityWithCommentType: EntityWithCommentType;
  initialState: FormMode;
  comment?: PtwCommentDto;
}>();

// --- EMITS ---
const emit = defineEmits<{
  create: [PtwCommentDto];
  update: [PtwCommentDto];
  delete: [string];
}>();

// --- STATE ---
const mode = ref(props.initialState);
const touched = ref(false);
const user = ref(props.comment ? props.comment.creatorUser : useAccountStore().currentUser);
const { t } = useTranslation();

interface CommentFormValues {
  description: string;
  attachments: FileMetaInfoDto[];
}

const { form } = useVeeForm<CommentFormValues>({
  mode: mode.value === 'add' ? 'add' : 'edit',
  options: {
    initialValues: {
      description: props.comment?.description || '',
      attachments: props.comment?.attachments || [],
    },
  },
  // No schema needed, but this enables upload validation tracking
  customValidateFunctions: [],
});

const { handleSubmit, resetForm, isSubmitting, values } = form;

// Bind textarea to form field
const { value: descriptionValue } = useField<string>('description');

// --- VIEW EVENT HANDLERS ---
function inputClickHandler() {
  if (mode.value === 'add') {
    touched.value = true;
  }
}

function cancelButtonHandler() {
  mode.value = props.initialState === 'add' ? 'add' : 'details';
  resetForm();
  touched.value = false;
}

// --- CRUD OPERATIONS ---
const { createComment, updateComment, deleteComment } = useCrudForComments(
  props.entityId,
  props.entityWithCommentType,
);

const submitHandler = handleSubmit(async (formValues) => {
  if (props.initialState === 'add') {
    const result = await createComment(formValues.description || '', formValues.attachments);
    if (result) {
      emit('create', result);
      resetForm();
      mode.value = 'add';
      touched.value = false;
    }
  } else {
    if (!props.comment) return;
    const result = await updateComment(
      props.comment.id,
      formValues.description || '',
      formValues.attachments,
    );
    if (result) {
      mode.value = 'details';
      touched.value = false;
      emit('update', result);
    }
  }
});

// delete overlay
const { action: deleteAction } = useActionWithConfirmation({
  confirmedAction: async () => {
    const id = props.comment?.id;
    if (!id) return;
    const result = await deleteComment(props.comment.id);
    if (result) {
      emit('delete', id);
    }
  },
  confirmationOptions: {
    ...getDefaultDeleteConfirmationOptions(
      {
        id: 0,
        name:
          props.comment?.description?.length && props.comment?.description?.length > 30
            ? props.comment?.description?.substring(0, 30) + '...'
            : props.comment?.description || '',
        typeName: 'Comment_one',
      },
      t,
    ),
  },
});

const deleteHandler = async () => {
  await deleteAction({ param: null });
};

// --- VIEW HELPER FUNCTIONS ---
const showSubmitOrEditButton = computed(() => touched.value || mode.value === 'edit');

const isButtonDisabled = computed(() => {
  // Only disable if there's no content - validation errors will prevent submission
  return (
    (!values.description || values.description?.length === 0) &&
    (!values.attachments || values.attachments.length === 0)
  );
});

const contextMenuActions = computed<ContextMenuAction[]>(() => {
  if (!props.comment) return [];

  const defaultContextMenuActions = getDefaultContextMenuActions(props.comment);
  return [
    {
      ...defaultContextMenuActions.edit,
      callback: () => {
        mode.value = 'edit';
        touched.value = true;
      },
    },
    {
      ...defaultContextMenuActions.delete,
      divider:
        (props.comment.possibleActions?.length && props.comment.possibleActions?.length > 1) ||
        false,
      callback: deleteHandler,
    },
  ];
});
</script>

<template>
  <form
    ref="tileRef"
    :class="{
      'comment-tile': true,
      'comment-tile--border': mode !== 'add',
    }"
    @submit.prevent="submitHandler"
  >
    <!-- colum 1 -->
    <div class="comment-tile__colum0">
      <!-- Avatar -->
      <span v-if="user" class="avatar">
        <AvatarImage :pictureId="user.profilePictureId" :tooltip="user.fullName" />
      </span>
    </div>

    <!-- colum 2 -->
    <div class="comment-tile__colum1">
      <div v-if="comment" class="comment-tile__header">
        <!-- meta info -->
        <CommentMetaInfo :commentDto="comment" data-testid="comment_metaInfo" />

        <!-- context menu -->
        <ContextMenu
          v-if="mode === 'details' && contextMenuActions.length"
          :actions="contextMenuActions"
          data-testid="comment_contextMenu"
        />
      </div>

      <!-- tags -->
      <div v-if="!!comment?.tags?.length" class="w-100 mb-4">
        <v-chip v-for="tag in comment?.tags" :key="tag">
          {{ $t(`StateOfUseEnum.${tag}`) }}
        </v-chip>
      </div>

      <!-- text intput / output -->
      <textarea
        v-if="mode === 'add' || mode === 'edit'"
        v-model="descriptionValue"
        :placeholder="t('AddAComment')"
        :class="
          'v-field__input ' +
          'comment-tile__text-area ' +
          (touched ? 'comment-tile__text-area--expanded' : '')
        "
        data-testid="comment_textContent"
        @click="inputClickHandler"
      ></textarea>

      <Markdown
        v-else
        :value="descriptionValue"
        class="comment-tile__text-display"
        data-testid="comment_viewTextContent"
      />

      <!-- attachments with buttons inline when editing/adding -->
      <div v-if="mode === 'add' || mode === 'edit'" class="actions-row">
        <div class="attachment-wrapper">
          <VeeAttachments
            name="attachments"
            :mode="mode"
            data-testid="Comment_Attachments"
            @click="inputClickHandler"
          />
        </div>

        <div v-if="showSubmitOrEditButton" class="buttons">
          <Button
            variant="text"
            size="small"
            data-testid="comment_cancelBtn"
            @click="cancelButtonHandler"
          >
            {{ t('Cancel') }}
          </Button>
          <Button
            variant="primary"
            size="small"
            type="submit"
            :disabled="isButtonDisabled"
            :loading="isSubmitting"
            data-testid="comment_addBtn"
          >
            {{ mode === 'add' ? t('AddComment') : t('Save') }}
          </Button>
        </div>
      </div>

      <!-- attachments in details mode -->
      <div v-else class="attachment-wrapper-details">
        <VeeAttachments name="attachments" :mode="mode" data-testid="Comment_Attachments" />
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.comment-tile {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 0px;
  padding: 16px 0;
  background: rgb(var(--v-theme-off-white));
  border-radius: 2px;
  height: fit-content;
  min-height: 64px;
  transition: height 0.5s ease;
}

.comment-tile:first-child {
  margin-bottom: 40px;
}

.comment-tile__header {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.comment-tile--border:not(:nth-child(2)) {
  border-top: 1px solid rgb(var(--v-theme-primary-lighten-4));
}

.comment-tile--expanded {
  min-height: 199px;
}

.comment-tile__colum0 {
  width: 40px;
}

.comment-tile__colum1 {
  flex-grow: 10;
  display: flex;
  flex-direction: column;
  align-items: end;
}

.comment-tile__text-area,
.comment-tile__text-display {
  width: 100%;
  white-space: pre-wrap;
}

.comment-tile__user-name {
  width: 100%;
}

.comment-tile__text-area {
  border: 1px solid rgb(var(--v-theme-primary-lighten-4));
  transition: height 0.5s ease;
  height: 50px;
  resize: none;
  padding: 10px;
  margin-bottom: 16px;
}

.comment-tile__text-area--expanded {
  height: 200px;
  transition: height 0.5s ease;
}

.actions-row {
  width: 100%;
  display: grid;
  grid-template-columns: 50% 1fr;
  align-items: flex-start;
  gap: 16px;
}

.attachment-wrapper {
  grid-column: 1;
}

.attachment-wrapper-details {
  width: 100%;
}

.buttons {
  grid-column: 2;
  display: flex;
  gap: 20px;
  justify-self: end;
}
</style>
